import React, { useState, useEffect, useMemo } from 'react';
import { X, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { getEvent } from '@/lib/api';
import { getTimeRemaining } from '@/lib/utils';

// Countdown Timer TimeBlock component
const TimeBlock = ({ value, label }) => (
  <div className="flex flex-col items-center">
    <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-secondary/80 border border-border flex items-center justify-center">
      <span className="text-2xl md:text-3xl font-bold text-white tabular-nums">
        {String(value).padStart(2, '0')}
      </span>
    </div>
    <span className="text-xs text-primary uppercase tracking-wider mt-2">{label}</span>
  </div>
);

const CountdownTimer = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState(getTimeRemaining(targetDate));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getTimeRemaining(targetDate));
    }, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);

  return (
    <div className="flex items-start justify-center gap-2 md:gap-4">
      <TimeBlock value={timeLeft.days} label="Days" />
      <span className="text-2xl text-muted-foreground mt-5 md:mt-6">:</span>
      <TimeBlock value={timeLeft.hours} label="Hours" />
      <span className="text-2xl text-muted-foreground mt-5 md:mt-6">:</span>
      <TimeBlock value={timeLeft.minutes} label="Minutes" />
      <span className="text-2xl text-muted-foreground mt-5 md:mt-6">:</span>
      <TimeBlock value={timeLeft.seconds} label="Seconds" />
    </div>
  );
};

/**
 * EventDetailsModal
 * 
 * Props:
 * - isOpen: boolean - Whether modal is visible
 * - onClose: function - Called when modal should close
 * - eventId: string - ID of event to display
 * - event: object (optional) - Event data if already loaded (avoids extra API call)
 */
export function EventDetailsModal({ isOpen, onClose, eventId, event: propEvent }) {
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [fetchedEvent, setFetchedEvent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Use prop event if provided, otherwise use fetched event
  const event = propEvent || fetchedEvent;

  // Fetch event data only if not provided as prop and modal is open
  useEffect(() => {
    // Skip if modal is closed, event is provided, or no eventId
    if (!isOpen || propEvent || !eventId) {
      return;
    }
    
    let cancelled = false;
    
    const fetchEventData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const response = await getEvent(eventId);
        if (cancelled) return;
        
        if (response.success && response.data) {
          setFetchedEvent(response.data);
        } else {
          setError('Event not found');
        }
      } catch (err) {
        if (cancelled) return;
        console.error('[EventDetailsModal] Failed to fetch event:', err);
        setError(err.message || 'Failed to load event');
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };
    
    fetchEventData();
    
    return () => {
      cancelled = true;
    };
  }, [isOpen, eventId, propEvent]);

  // Reset image index when modal opens with new event
  const eventIdForReset = useMemo(() => event?.id, [event?.id]);
  useEffect(() => {
    setCurrentImageIndex(0);
  }, [eventIdForReset]);

  if (!isOpen) return null;

  // Loading state
  if (loading && !event) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <Card className="relative z-10 w-full max-w-md bg-card border-border p-8 flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading event details...</p>
        </Card>
      </div>
    );
  }

  // Error state
  if (error || !event) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
        <Card className="relative z-10 w-full max-w-md bg-card border-border p-8 text-center">
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-secondary hover:bg-secondary/80 text-white"
          >
            <X className="w-5 h-5" />
          </button>
          <p className="text-red-400 mb-4">{error || 'Event not found'}</p>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </Card>
      </div>
    );
  }

  const isPastEvent = event.status === 'past';

  const handlePurchaseTicket = () => {
    onClose();
    navigate(`/events/${eventId}/tickets`);
  };

  const handleWatchRecap = () => {
    onClose();
    navigate(`/events/${eventId}/recap`);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="event-details-modal">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <Card className="relative z-10 w-full max-w-4xl bg-card border-border max-h-[90vh] overflow-hidden flex flex-col">
        {/* Close Button */}
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 z-20 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white"
          data-testid="modal-close-button"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="overflow-y-auto flex-1">
          {/* Image Carousel */}
          <div className="relative aspect-[16/9]">
            <img 
              src={event.coverImageUrl || event.image} 
              alt={event.title}
              className="w-full h-full object-cover"
            />
            {/* Navigation arrows */}
            <button 
              className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-primary transition-colors"
              onClick={() => setCurrentImageIndex(prev => Math.max(0, prev - 1))}
              data-testid="modal-prev-image"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>
            <button 
              className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 flex items-center justify-center text-white hover:bg-primary transition-colors"
              onClick={() => setCurrentImageIndex(prev => prev + 1)}
              data-testid="modal-next-image"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
            {/* Pagination dots */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
              {[0, 1, 2].map(i => (
                <div 
                  key={i} 
                  className={`w-2.5 h-2.5 rounded-full transition-colors ${i === currentImageIndex ? 'bg-primary' : 'bg-white/50'}`}
                />
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 md:p-8 space-y-8">
            {/* Title */}
            <h1 className="text-2xl md:text-3xl font-bold text-white" data-testid="event-modal-title">{event.title}</h1>

            {/* Event Info */}
            <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
              <span>📍 {event.venue || event.location || 'TBA'}</span>
              <span>📅 {event.time || new Date(event.date).toLocaleDateString()}</span>
              {event.isLive && <span className="text-red-500 font-medium">🔴 LIVE NOW</span>}
            </div>

            {/* Countdown / Past Event */}
            {isPastEvent ? (
              <div className="bg-secondary/50 rounded-xl p-6 text-center">
                <p className="text-muted-foreground mb-4">This event has ended</p>
                {event.hasRecap && (
                  <Button 
                    onClick={handleWatchRecap}
                    className="bg-violet-600 hover:bg-violet-700 text-white h-12 px-10 font-semibold"
                    data-testid="watch-recap-button"
                  >
                    Watch Event Recap
                  </Button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-muted-foreground text-sm text-center">Event Starts In</p>
                <CountdownTimer targetDate={event.date} />
              </div>
            )}

            {/* Description */}
            <div className="space-y-3">
              <h3 className="text-lg font-semibold text-white">Description</h3>
              <p className="text-muted-foreground leading-relaxed">
                {event.description || 'No description available.'}
              </p>
            </div>

            {/* Artists */}
            {event.artists && event.artists.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-white">Artists</h3>
                <div className="flex gap-5 overflow-x-auto pb-2 scrollbar-hide">
                  {event.artists.map(artist => (
                    <button
                      key={artist.id || artist}
                      onClick={(e) => {
                        e.stopPropagation();
                        onClose();
                        navigate(`/artists/${artist.id || artist}`);
                      }}
                      className="flex-shrink-0 text-center cursor-pointer group"
                      data-testid={`artist-link-${artist.id || artist}`}
                    >
                      {artist.image && (
                        <img 
                          src={artist.image} 
                          alt={artist.name || artist}
                          className="w-16 h-16 md:w-20 md:h-20 rounded-full object-cover border-2 border-border group-hover:border-primary transition-colors"
                        />
                      )}
                      <p className="text-xs text-muted-foreground mt-2 max-w-[80px] truncate group-hover:text-primary transition-colors">
                        {artist.name || artist}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Simplified Ticket Purchase - Single Button */}
            {!isPastEvent && (
              <div className="flex justify-center pt-4">
                <Button 
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-14 px-12 text-lg font-semibold"
                  onClick={handlePurchaseTicket}
                  data-testid="purchase-ticket-button"
                >
                  Purchase Ticket
                </Button>
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}

export default EventDetailsModal;
